import z from 'zod/v4';
import { FileSchema } from '../files/index.js';
import { Currency, Entity } from '../index.js';
export var PropertyDataType;
(function (PropertyDataType) {
    PropertyDataType["checkbox"] = "checkbox";
    PropertyDataType["datePicker"] = "date-picker";
    PropertyDataType["dropdown"] = "dropdown";
    PropertyDataType["singleLineText"] = "single-line-text";
    PropertyDataType["multiLineText"] = "multi-line-text";
    PropertyDataType["number"] = "number";
    PropertyDataType["file"] = "file";
    PropertyDataType["address"] = "address";
    PropertyDataType["money"] = "money";
})(PropertyDataType || (PropertyDataType = {}));
/**
 * The kinds of thing a property type can be narrowed to. Only case groups are
 * modelled today; the shape is a tagged union so a further kind is a new member
 * rather than a new column, and every `switch` over it fails to compile until
 * the new kind is handled.
 */
export var PropertyScopeType;
(function (PropertyScopeType) {
    PropertyScopeType["CaseGroup"] = "case-group";
})(PropertyScopeType || (PropertyScopeType = {}));
export const PropertyScopeSchema = z.discriminatedUnion('type', [
    z.object({
        type: z.literal(PropertyScopeType.CaseGroup),
        id: z.uuid(),
    }),
]);
export const PatchPropertyTypeSchema = z.object({
    /** What the type is narrowed to; empty means every case. */
    scopes: z.array(PropertyScopeSchema).optional(),
    data: z
        .object({
        options: z.array(z.object({
            text: z.string(),
            value: z.string(),
        })),
    })
        .optional(),
    label: z.string().optional(),
    description: z.string().optional(),
    required: z.boolean().optional(),
    searchable: z.boolean().optional(),
});
export const PropertyRefSchema = z.enum([
    Entity.Case,
    Entity.CaseVoucher,
    Entity.Creditor,
    Entity.Customer,
]);
export var ValidatedPropertyHandles;
(function (ValidatedPropertyHandles) {
    ValidatedPropertyHandles["email"] = "email";
    ValidatedPropertyHandles["name"] = "name";
    ValidatedPropertyHandles["phone"] = "phone";
})(ValidatedPropertyHandles || (ValidatedPropertyHandles = {}));
// Make common
export const AddressSchema = z.object({
    address: z.string(),
    coAddress: z.string().nullish(),
    zipcode: z.string(),
    city: z.string(),
    country: z.string().nullish(),
    door: z.string().nullish(),
    floor: z.string().nullish(),
    houseNoFrom: z.number().nullish(),
    level: z.string().nullish(),
    formatted: z.string().nullish(),
    addressTwo: z.string().nullish(),
    street: z.string().nullish(),
    dawsId: z.string().nullish(),
    text: z.string().nullish(),
    longitiude: z.number().nullish(),
    latitude: z.number().nullish(),
});
export const DataScheme = z.union([
    z.object({
        type: z.literal(PropertyDataType.checkbox),
        data: z.boolean().nullish(),
    }),
    // multi- and single-line-text
    z.object({
        type: z.union([
            z.literal(PropertyDataType.singleLineText),
            z.literal(PropertyDataType.multiLineText),
            z.literal(PropertyDataType.dropdown),
        ]),
        data: z.string().nullish(),
    }),
    // number
    z.object({
        type: z.literal(PropertyDataType.number),
        data: z.number().nullish(),
    }),
    // date
    z.object({
        type: z.literal(PropertyDataType.datePicker),
        data: z.string().nullish(), // TODO: ISO date
    }),
    // address
    z.object({
        type: z.literal(PropertyDataType.address),
        data: AddressSchema,
    }),
    // money
    z.object({
        type: z.literal(PropertyDataType.money),
        data: z
            .object({
            amount: z.number().int(),
            currency: z.string(),
        })
            .nullish(),
    }),
    // file
    z.object({
        type: z.literal(PropertyDataType.file),
        data: z
            .object({
            fileId: z.string(),
            contentType: z.string(),
            fileName: z.string(),
        })
            .nullish(),
    }),
]);
export const PropertySchema = z
    .object({
    ref: z.union([
        z.literal(Entity.Case),
        z.literal(Entity.CaseVoucher),
        z.literal(Entity.Creditor),
        z.literal(Entity.Customer),
        z.literal(Entity.Collector),
    ]),
    refId: z.uuid(),
    handle: z.string(),
})
    .and(DataScheme);
export const AddPropertyWithoutRefSchema = z
    .object({
    handle: z.string(),
})
    .and(DataScheme);
export const PropertyDataSchema = z
    .union([
    z.string(),
    z.number(),
    z.boolean(),
    z.object({
        amount: z.number(),
        currency: z.enum(Currency),
    }),
    z.object({
        fileId: z.uuid(),
        contentType: z.string(),
        fileName: z.string(),
    }),
    AddressSchema,
])
    .nullable();
export const OldPropertiesSchema = z.object({
    handle: z.string(),
    data: z.union([
        z.object({
            text: z.string(),
        }),
        z.object({
            value: z.string(),
        }),
        z.object({
            date: z.string(),
        }),
        z.object({
            amount: z.number(),
            currency: z.enum(Currency),
        }),
        z.object({
            checked: z.boolean(),
        }),
    ]),
});
export const normalizePropertiesInput = (properties) => {
    if (Array.isArray(properties)) {
        // Old deprcated properties format
        return properties.reduce((acc, property) => {
            if (!property) {
                return acc;
            }
            // @ts-ignore
            if (typeof property.data?.text !== 'undefined') {
                // @ts-ignore
                acc[property.handle] = property.data?.text;
                // @ts-ignore
            }
            else if (typeof property.data?.number !== 'undefined') {
                // @ts-ignore
                acc[property.handle] = property.data?.number;
                // @ts-ignore
            }
            else if (typeof property.data?.value !== 'undefined') {
                // @ts-ignore
                acc[property.handle] = property.data?.value;
                // @ts-ignore
            }
            else if (typeof property.data?.date !== 'undefined') {
                // @ts-ignore
                acc[property.handle] = property.data?.date;
                // @ts-ignore
            }
            else if (typeof property.data?.checked !== 'undefined') {
                // @ts-ignore
                acc[property.handle] = property.data?.checked;
            }
            else {
                // @ts-ignore
                acc[property.handle] = property.data;
            }
            return acc;
        }, {});
    }
    else {
        return properties;
    }
};
export const addRefIdToProperties = (properties, ref, refId) => properties.map((property) => ({
    ...property,
    refId: refId,
    ref: ref,
}));
export const listToObject = (properties) => {
    return properties.reduce((acc, property) => {
        acc[property.handle] = property.data;
        return acc;
    }, {});
};
/** The case groups a type names, ignoring scopes of any other kind. */
export const caseGroupScopeIds = (scopes) => scopes
    .filter((scope) => scope.type === PropertyScopeType.CaseGroup)
    .map((scope) => scope.id);
export const caseGroupScopes = (caseGroupIds) => caseGroupIds.map((id) => ({ type: PropertyScopeType.CaseGroup, id }));
/**
 * An unscoped type applies to every case; a scoped one applies wherever any one
 * of its scopes matches.
 */
export const appliesToCaseGroup = (propertyType, caseGroupId) => propertyType.scopes.length === 0 ||
    propertyType.scopes.some((scope) => scope.type === PropertyScopeType.CaseGroup && scope.id === caseGroupId);
export const filterPropertyTypes = (propertyTypes, ref, caseGroupId) => propertyTypes.filter((type) => type.ref === ref && appliesToCaseGroup(type, caseGroupId));
export const validateProperty = (propertyType, data, disableRequired) => {
    try {
        switch (propertyType.type) {
            case PropertyDataType.singleLineText:
            case PropertyDataType.multiLineText:
                if (propertyType.required && !disableRequired) {
                    z.string().parse(data);
                }
                else {
                    z.string().nullable().optional().parse(data);
                }
                break;
            case PropertyDataType.checkbox:
                if (propertyType.required && !disableRequired) {
                    z.boolean().parse(data);
                }
                else {
                    z.boolean().nullable().optional().parse(data);
                }
                break;
            case PropertyDataType.datePicker:
                if (propertyType.required && !disableRequired) {
                    z.string().datetime({ offset: true }).parse(data);
                }
                else {
                    z.string()
                        .datetime({ offset: true })
                        .nullable()
                        .optional()
                        .parse(data);
                }
                break;
            case PropertyDataType.dropdown:
                const values = propertyType.data?.options.map((option) => option.value) || [];
                if (values.length > 0) {
                    if (propertyType.required && !disableRequired) {
                        z.enum(values).parse(data);
                    }
                    else {
                        z.string().nullable().optional().parse(data);
                    }
                }
                break;
            case PropertyDataType.number:
                if (propertyType.required && !disableRequired) {
                    z.number().parse(data);
                }
                else {
                    z.number().nullable().optional().parse(data);
                }
                break;
            case PropertyDataType.file:
                if (propertyType.required && !disableRequired) {
                    FileSchema.parse(data);
                }
                else {
                    FileSchema.nullable().optional().parse(data);
                }
                break;
            case PropertyDataType.address:
                if (propertyType.required && !disableRequired) {
                    AddressSchema.parse(data);
                }
                else {
                    AddressSchema.nullable().optional().parse(data);
                }
                break;
            case PropertyDataType.money:
                if (propertyType.required && !disableRequired) {
                    z.object({
                        currency: z.enum(Currency),
                        amount: z.number(),
                    }).parse(data);
                }
                else {
                    z.object({
                        currency: z.enum(Currency),
                        amount: z.number(),
                    })
                        .nullable()
                        .optional()
                        .parse(data);
                }
                break;
            default:
                throw new Error('Property type not handled');
        }
    }
    catch (error) {
        // @ts-ignore
        const zMessage = error?.errors && error?.errors[0]?.message;
        const message = zMessage
            ? `Property ${propertyType.handle} (${propertyType.type}): ${zMessage}`
            : `Property ${propertyType.handle} (${propertyType.type}) not valid`;
        throw new Error(message, { cause: error });
    }
};
//# sourceMappingURL=index.js.map